import { useState, useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Chip,
  Button,
  ButtonGroup,
  Paper,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import TableChartIcon from '@mui/icons-material/TableChart';
import CodeIcon from '@mui/icons-material/Code';
import {
  META_KEYS,
  TIME_FIELDS,
  PCT_FIELDS,
  extractMitrolData,
  formatSeconds,
  formatPct,
  formatDate,
  exportCSV,
} from '../helpers/mitrolHelpers';

const MitrolDataTable = ({ data, panelId }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [view, setView] = useState('table');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const mitrol = useMemo(() => extractMitrolData(data), [data]);

  const allKeys = useMemo(() => {
    if (!mitrol || mitrol.rows.length === 0) return [];
    return Object.keys(mitrol.rows[0]).filter((k) => !META_KEYS.includes(k));
  }, [mitrol]);

  const headerMap = useMemo(() => {
    if (!mitrol) return {};
    const map = {};
    for (const key of allKeys) {
      map[key] = mitrol.labels ? mitrol.labels[key] || key : key;
    }
    return map;
  }, [mitrol, allKeys]);

  const rows = mitrol?.rows || [];
  const labels = mitrol?.labels || null;

  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return rows.slice(start, start + rowsPerPage);
  }, [rows, page, rowsPerPage]);

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  if (!mitrol || rows.length === 0) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">
          Respuesta
        </Typography>
        <Paper
          variant="outlined"
          sx={{
            mt: 1,
            p: 2,
            maxHeight: 500,
            overflow: 'auto',
            bgcolor: isDarkMode ? '#0f172a' : '#f8fafc',
          }}
        >
          <pre style={{ margin: 0, fontSize: '0.8rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
            {data ? JSON.stringify(data, null, 2) : '// La respuesta aparecerá aquí...'}
          </pre>
        </Paper>
      </Box>
    );
  }

  const getCellStyle = (key, raw) => {
    if (key === 'Intervalo') return {};
    if (TIME_FIELDS.includes(key))
      return {
        textAlign: 'right',
        fontFamily: 'Consolas, monospace',
        color: raw == 0 ? (isDarkMode ? '#475569' : '#94a3b8') : (isDarkMode ? '#67e8f9' : '#0891b2'),
      };
    if (PCT_FIELDS.includes(key))
      return {
        textAlign: 'right',
        fontFamily: 'Consolas, monospace',
        color: raw == 0 ? (isDarkMode ? '#475569' : '#94a3b8') : (isDarkMode ? '#a78bfa' : '#7c3aed'),
      };
    if (!isNaN(raw) && raw !== '')
      return {
        textAlign: 'right',
        fontFamily: 'Consolas, monospace',
        color: raw == 0 ? (isDarkMode ? '#475569' : '#94a3b8') : undefined,
      };
    return {};
  };

  const formatCell = (key, raw) => {
    if (key === 'Intervalo') return formatDate(raw);
    if (TIME_FIELDS.includes(key)) return formatSeconds(raw);
    if (PCT_FIELDS.includes(key)) return formatPct(raw);
    return raw ?? '';
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="subtitle2" color="text.secondary">
            Respuesta
          </Typography>
          <Chip label={`${rows.length} filas`} size="small" color="primary" />
          <Chip label={`${allKeys.length} columnas`} size="small" variant="outlined" />
          {data?.periodo && (
            <Typography variant="caption" color="text.secondary">
              Periodo: {data.periodo.desde} a {data.periodo.hasta}
            </Typography>
          )}
          {data?.reporte && (
            <Typography variant="caption" color="text.secondary">
              | Reporte: {data.reporte}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <ButtonGroup size="small">
            <Button
              variant={view === 'table' ? 'contained' : 'outlined'}
              onClick={() => setView('table')}
              startIcon={<TableChartIcon />}
            >
              Tabla
            </Button>
            <Button
              variant={view === 'json' ? 'contained' : 'outlined'}
              onClick={() => setView('json')}
              startIcon={<CodeIcon />}
            >
              JSON
            </Button>
          </ButtonGroup>
          <Button
            size="small"
            variant="outlined"
            color="success"
            startIcon={<FileDownloadIcon />}
            onClick={() => exportCSV(rows, labels, allKeys)}
          >
            CSV
          </Button>
        </Box>
      </Box>

      {view === 'table' ? (
        <>
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{
            maxHeight: 600,
            bgcolor: isDarkMode ? '#0f172a' : '#fff',
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    bgcolor: isDarkMode ? '#1e293b' : '#f1f5f9',
                    color: isDarkMode ? '#38bdf8' : '#0284c7',
                  }}
                >
                  #
                </TableCell>
                {allKeys.map((key) => (
                  <TableCell
                    key={key}
                    title={key}
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.7rem',
                      textTransform: 'uppercase',
                      whiteSpace: 'nowrap',
                      bgcolor: isDarkMode ? '#1e293b' : '#f1f5f9',
                      color: isDarkMode ? '#38bdf8' : '#0284c7',
                    }}
                  >
                    {headerMap[key]}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRows.map((row, i) => (
                <TableRow
                  key={page * rowsPerPage + i}
                  hover
                  sx={{
                    '&:nth-of-type(even)': {
                      bgcolor: isDarkMode ? 'rgba(30,41,59,0.4)' : 'rgba(241,245,249,0.5)',
                    },
                  }}
                >
                  <TableCell
                    sx={{
                      fontFamily: 'Consolas, monospace',
                      textAlign: 'right',
                      fontSize: '0.75rem',
                    }}
                  >
                    {page * rowsPerPage + i + 1}
                  </TableCell>
                  {allKeys.map((key) => {
                    const raw = row[key] ?? '';
                    return (
                      <TableCell
                        key={key}
                        sx={{
                          whiteSpace: 'nowrap',
                          fontSize: '0.75rem',
                          ...getCellStyle(key, raw),
                        }}
                      >
                        {String(formatCell(key, raw))}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={rows.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[25, 50, 100, 250, 500]}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          sx={{ borderTop: 1, borderColor: isDarkMode ? '#334155' : '#e0e0e0' }}
        />
        </>
      ) : (
        <Paper
          variant="outlined"
          sx={{
            mt: 1,
            p: 2,
            maxHeight: 500,
            overflow: 'auto',
            bgcolor: isDarkMode ? '#0f172a' : '#f8fafc',
          }}
        >
          <pre style={{ margin: 0, fontSize: '0.8rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
            {JSON.stringify(data, null, 2)}
          </pre>
        </Paper>
      )}
    </Box>
  );
};

export default MitrolDataTable;
